import * as React from 'react';
import { CircleCheckIcon } from '@revfluence/fresh-icons/solid/esm';

/**
 * Represents a single option card in the group.
 * @template T The type of the value associated with the option.
 */
export interface ISelectableCardOption<T> {
  id: string | number; // Unique key for React rendering
  value: T; // The actual value passed to callbacks
  renderBody: React.ReactNode; // Body content provided by the consumer
  disabled?: boolean; // Is the option selectable?
}

interface ISelectableCardGroupProps<T> {
  /** The currently selected value. */
  value: T | null;
  /** Array of options to display. */
  options: ISelectableCardOption<T>[];
  /** Callback when a card is selected. */
  onChange: (value: T) => void;
  /** Orientation of the cards. */
  orientation?: 'horizontal' | 'vertical';
}

/**
 * A component displaying a group of selectable cards, often used like a radio group.
 * Handles wrapping card styling, selection state, indicator, and interaction logic.
 * The content of each card is provided via the `renderBody` prop.
 * @template T The type of the value associated with each option.
 */
export const SelectableCardGroup = <T extends string | number>({
  value,
  options,
  onChange,
  orientation = 'horizontal',
}: ISelectableCardGroupProps<T>) => {
  const groupRef = React.useRef<HTMLDivElement>(null);

  return (
    <div
      ref={groupRef}
      className={`grid pr-1 ${orientation === 'horizontal' ? 'grid-cols-1 md:grid-cols-2 gap-4' : 'grid-rows-1 md:grid-rows-2 gap-4'}`}
      role="radiogroup"
    >
      {options.map((option) => {
        const isSelected = value === option.value && !option.disabled;

        return (
          <div
            key={option.id}
            className={`${
              isSelected
                ? 'ring-1 ring-primary'
                : 'border border-border hover:border-grey'
            } ${
              option.disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
            } rounded-2xl px-4 py-3 relative transition-colors`}
            onClick={() => !option.disabled && onChange(option.value)}
            role="radio"
            aria-checked={isSelected}
            aria-disabled={option.disabled}
            tabIndex={0}
          >
            {isSelected && (
              <CircleCheckIcon fontSize={16} className="absolute -top-1 -right-1 bg-white" />
            )}
            {option.renderBody}
          </div>
        );
      })}
    </div>
  );
};

SelectableCardGroup.displayName = 'SelectableCardGroup';
